import { useMemo, useReducer, useRef } from 'react';

import type { IAppPreviewDispatchContext } from './AppPreviewDispatchContext';
import { AppPreviewDispatchContext } from './AppPreviewDispatchContext';
import { makeAppPreviewReducer } from './AppPreviewReducer';
import { initialAppPreviewState } from './AppPreviewState';
import { AppPreviewStateContext } from './AppPreviewStateContext';

type IAppPreviewProviderProps = {
  /**
   * The child nodes your Provider has wrapped.
   */
  children?: React.ReactNode;
};

export const AppPreviewProvider: React.FC<IAppPreviewProviderProps> = ({
  children,
}) => {
  const savedStateRef = useRef(initialAppPreviewState);
  const [state, dispatch] = useReducer(
    makeAppPreviewReducer(savedStateRef),
    initialAppPreviewState,
  );

  const dispatchContextValue = useMemo<IAppPreviewDispatchContext>(
    () => ({
      navigateToRoute: (route) =>
        dispatch({
          type: 'NAVIGATE_TO_ROUTE',
          route,
        }),
      navigateToUrl: (url) =>
        dispatch({
          type: 'NAVIGATE_TO_URL',
          url,
        }),
      setErrorMessage: (errorMessage) =>
        dispatch({
          type: 'SET_ERROR_MESSAGE',
          errorMessage,
        }),
      setOpened: (opened, options) =>
        dispatch({
          type: 'SET_OPENED',
          opened,
          options,
        }),
      setHidden: (hidden) =>
        dispatch({
          type: 'SET_HIDDEN',
          hidden,
        }),
      setIframeRef: (iframeRef) =>
        dispatch({
          type: 'SET_IFRAME_REF',
          iframeRef,
        }),
      setSize: (size, options) =>
        dispatch({
          type: 'SET_SIZE',
          size,
          options,
        }),
      saveState: () => dispatch({ type: 'SAVE_STATE' }),
      restoreState: () => dispatch({ type: 'RESTORE_STATE' }),
    }),
    [],
  );

  return (
    <AppPreviewStateContext.Provider value={state}>
      <AppPreviewDispatchContext.Provider value={dispatchContextValue}>
        {children}
      </AppPreviewDispatchContext.Provider>
    </AppPreviewStateContext.Provider>
  );
};
